/**
 * Public website API wrapper. Targets the backend at VITE_BACKEND_URL (baked at
 * build time). Only the unauthenticated public form endpoints are used here.
 */

export const BACKEND_URL =
  (import.meta.env.VITE_BACKEND_URL as string | undefined) ??
  "https://api.wiederdabei.de";

export type FormKind = "contact" | "shop";

export interface ContactPayload {
  name: string;
  email: string;
  subject: string;
  message: string;
  /** Altcha widget solution (base64 payload). */
  altcha: string;
  /** Honeypot — must stay empty for real users. */
  hp?: string;
}

export interface SubmitResult {
  ok: boolean;
  /** Backend error token when ok=false. */
  error?: string;
  /** HTTP status (0 on network failure). */
  status: number;
}

/** Body of `POST /beta-interest`. All content fields optional; altcha required. */
export interface BetaPayload {
  name?: string;
  email?: string;
  situation?: string;
  expertise?: string;
  /** Preferred testing window start (YYYY-MM-DD). */
  dateFrom?: string;
  /** Preferred testing window end (YYYY-MM-DD). */
  dateTo?: string;
  /** Altcha widget solution (base64 payload). */
  altcha: string;
  /** Honeypot — must stay empty for real users. */
  hp?: string;
}

/** Absolute URL of the Altcha challenge endpoint (used by the widget). */
export function altchaChallengeUrl(): string {
  return `${BACKEND_URL}/altcha/challenge`;
}

/** Result of requesting a demo token. Never throws — structured result. */
export interface DemoTokenResult {
  ok: boolean;
  /** The demo device bearer token (present on success). */
  token?: string;
  /** Epoch ms when the token expires (present on success). */
  expiresAt?: number;
  /** Demo room name (present on success). */
  room?: string;
  /** Backend error token when ok=false ("demo_disabled", "rate_limited", …). */
  error?: string;
  /** HTTP status (0 on network failure). */
  status: number;
}

/** Body of `POST /demo/token` — mirrors the contact form's captcha/honeypot. */
export interface DemoTokenPayload {
  /** Tester's email (required). Receives the demo token + set-password link. */
  email: string;
  /** Altcha widget solution (base64 payload). */
  altcha: string;
  /** Honeypot — must stay empty for real users. */
  hp?: string;
}

/**
 * Mint a 3-day demo device token via `POST /demo/token`. Requires the tester's
 * email plus an Altcha solution + honeypot (same pattern as the contact form).
 * Returns a structured result; a 403 (`demo_disabled`) is surfaced as ok=false
 * so the page can show the "Demo derzeit nicht verfügbar" state gracefully.
 */
export async function requestDemoToken(
  payload: DemoTokenPayload,
): Promise<DemoTokenResult> {
  try {
    const res = await fetch(`${BACKEND_URL}/demo/token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: payload.email,
        altcha: payload.altcha,
        hp: payload.hp ?? "",
      }),
    });
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    if (!res.ok) {
      const error =
        body && typeof body === "object" && "error" in body
          ? String((body as { error: unknown }).error)
          : `HTTP ${res.status}`;
      return { ok: false, error, status: res.status };
    }
    const b = (body ?? {}) as {
      token?: string;
      expiresAt?: number;
      room?: string;
    };
    return {
      ok: true,
      token: b.token,
      expiresAt: b.expiresAt,
      room: b.room,
      status: res.status,
    };
  } catch {
    return { ok: false, error: "network", status: 0 };
  }
}

/**
 * Fetch the public runtime config via `GET /public-config`. Never throws —
 * returns `{ beta: false }` on any error or non-ok response so beta UI stays
 * hidden when the backend is unreachable or the flag is missing.
 */
export async function fetchPublicConfig(): Promise<{ beta: boolean }> {
  try {
    const res = await fetch(`${BACKEND_URL}/public-config`);
    if (!res.ok) return { beta: false };
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    const beta =
      body && typeof body === "object" && "beta" in body
        ? Boolean((body as { beta: unknown }).beta)
        : false;
    return { beta };
  } catch {
    return { beta: false };
  }
}

/** POST a beta-interest submission. Never throws — returns a structured result. */
export async function submitBetaInterest(
  payload: BetaPayload,
): Promise<SubmitResult> {
  try {
    const res = await fetch(`${BACKEND_URL}/beta-interest`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: payload.name,
        email: payload.email,
        situation: payload.situation,
        expertise: payload.expertise,
        dateFrom: payload.dateFrom,
        dateTo: payload.dateTo,
        altcha: payload.altcha,
        hp: payload.hp ?? "",
      }),
    });
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    if (!res.ok) {
      const error =
        body && typeof body === "object" && "error" in body
          ? String((body as { error: unknown }).error)
          : `HTTP ${res.status}`;
      return { ok: false, error, status: res.status };
    }
    return { ok: true, status: res.status };
  } catch {
    return { ok: false, error: "network", status: 0 };
  }
}

/** POST a contact/shop submission. Never throws — returns a structured result. */
export async function submitForm(
  kind: FormKind,
  payload: ContactPayload,
): Promise<SubmitResult> {
  const path = kind === "shop" ? "/shop-interest" : "/contact";
  try {
    const res = await fetch(`${BACKEND_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    if (!res.ok) {
      const error =
        body && typeof body === "object" && "error" in body
          ? String((body as { error: unknown }).error)
          : `HTTP ${res.status}`;
      return { ok: false, error, status: res.status };
    }
    return { ok: true, status: res.status };
  } catch {
    return { ok: false, error: "network", status: 0 };
  }
}
